import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  FileText, 
  Shield, 
  Package, 
  Calculator,
  CheckCircle2,
  Award,
  TrendingUp,
  Users,
  Server,
  DollarSign,
  Download,
  Sparkles,
  Star,
  MessageSquare,
  Clock,
  CreditCard,
  Gift,
  Brain
} from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface DiagnosticTIPricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartQuestionnaire: () => void;
}

type Tab = 'overview' | 'methodology' | 'roi' | 'whybz' | 'pricing' | 'start';
type DocumentType = 'audit' | 'bilan' | 'package';
type QualityLevel = 'essentiel' | 'pro' | 'expert';

interface PricingTier {
  level: QualityLevel;
  name: string;
  icon: string;
  pages: string;
  duration: string;
  basePrice: number;
  pricePerEmployee: number;
  pricePerAsset: number;
  features: string[];
  notIncluded?: string[];
}

const AUDIT_PRICING: Record<QualityLevel, PricingTier> = {
  essentiel: {
    level: 'essentiel',
    name: 'Essentiel',
    icon: '🥉',
    pages: '~20 pages',
    duration: '2-3 semaines',
    basePrice: 3000,
    pricePerEmployee: 30,
    pricePerAsset: 50,
    features: [
      'Évaluation NIST CSF basique (6 fonctions)',
      'Score de sécurité global',
      'Top 10 vulnérabilités critiques',
      '10-15 recommandations prioritaires',
      'Roadmap 6 mois',
      'Sommaire exécutif'
    ],
    notIncluded: [
      'ISO 27001 détaillé',
      'Playbooks incidents',
      'Analyse conformité avancée'
    ]
  },
  pro: {
    level: 'pro',
    name: 'Professionnel',
    icon: '🥈',
    pages: '~45 pages',
    duration: '4-6 semaines',
    basePrice: 8000,
    pricePerEmployee: 50,
    pricePerAsset: 80,
    features: [
      'Évaluation NIST CSF 2.0 complète',
      'ISO 27001 (93 contrôles - synthèse)',
      'Top 20 vulnérabilités + cyber risk register',
      '20-30 recommandations avec coûts/ROI',
      'Roadmap 12 mois',
      '3 Playbooks incidents principaux',
      'Inventaires en annexe',
      'Analyse conformité Loi 25'
    ]
  },
  expert: {
    level: 'expert',
    name: 'Expert',
    icon: '🥇',
    pages: '~80 pages',
    duration: '8-12 semaines',
    basePrice: 18000,
    pricePerEmployee: 80,
    pricePerAsset: 120,
    features: [
      'TOUT du Professionnel +',
      'ISO 27001:2022 EXHAUSTIF (93 contrôles)',
      'CIS Controls v8 complet (18 contrôles)',
      'Scénarios d\'attaque détaillés',
      '30-40 recommandations ultra-détaillées',
      'Roadmap 24 mois avec budgets',
      '5+ Playbooks incidents complets',
      'Analyse conformité multi-juridictions',
      'Plan de test et validation'
    ]
  }
};

const BILAN_PRICING: Record<QualityLevel, PricingTier> = {
  essentiel: {
    level: 'essentiel',
    name: 'Essentiel',
    icon: '🥉',
    pages: '~20 pages',
    duration: '2-3 semaines',
    basePrice: 2500,
    pricePerEmployee: 25,
    pricePerAsset: 40,
    features: [
      'Évaluation COBIT basique',
      'Score de maturité global',
      'Analyse des 5 piliers TI',
      '10-15 recommandations',
      'Roadmap 6 mois',
      'Sommaire exécutif'
    ],
    notIncluded: [
      'ITIL 4 détaillé',
      'ISO 20000',
      'Benchmark sectoriel avancé'
    ]
  },
  pro: {
    level: 'pro',
    name: 'Professionnel',
    icon: '🥈',
    pages: '~45 pages',
    duration: '4-6 semaines',
    basePrice: 7000,
    pricePerEmployee: 45,
    pricePerAsset: 70,
    features: [
      'Évaluation COBIT 2019 complète (40 processus)',
      'ITIL 4 (34 pratiques)',
      'ISO 20000 (niveau synthèse)',
      'Spider charts et scoring détaillé',
      '20-30 recommandations avec ROI',
      'Roadmap stratégique 12 mois',
      'Analyse de maturité par domaine',
      'Inventaires en annexe'
    ]
  },
  expert: {
    level: 'expert',
    name: 'Expert',
    icon: '🥇',
    pages: '~80 pages',
    duration: '8-12 semaines',
    basePrice: 15000,
    pricePerEmployee: 70,
    pricePerAsset: 100,
    features: [
      'TOUT du Professionnel +',
      'COBIT 2019 EXHAUSTIF',
      'ITIL 4 + ISO 20000 complets',
      'CMMi v2.0 évaluation',
      '30-40 recommandations ultra-détaillées',
      'Roadmap 3 ans avec budgets CAPEX/OPEX',
      'Benchmark sectoriel complet',
      'Analyse coût-bénéfice détaillée',
      'Glossaire technique étendu'
    ]
  }
};

export default function DiagnosticTIPricingModal({ isOpen, onClose, onStartQuestionnaire }: DiagnosticTIPricingModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [documentType, setDocumentType] = useState<DocumentType>('package');
  const [qualityLevel, setQualityLevel] = useState<QualityLevel>('pro');
  const [employeeCount, setEmployeeCount] = useState<number>(50);
  const [assetCount, setAssetCount] = useState<number>(15);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['diagnostic_ti_last_ai_analysis'] ?? null) : null;

  const calculatePrice = (type: DocumentType, level: QualityLevel): number => {
    if (type === 'audit') {
      const tier = AUDIT_PRICING[level];
      return tier.basePrice + (employeeCount * tier.pricePerEmployee) + (assetCount * tier.pricePerAsset);
    } else if (type === 'bilan') {
      const tier = BILAN_PRICING[level];
      return tier.basePrice + (employeeCount * tier.pricePerEmployee) + (assetCount * tier.pricePerAsset);
    } else {
      // Package: 15% discount
      const auditPrice = calculatePrice('audit', level);
      const bilanPrice = calculatePrice('bilan', level);
      return Math.round((auditPrice + bilanPrice) * 0.85);
    }
  };

  const getTotalPrice = () => {
    return calculatePrice(documentType, qualityLevel);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-bz-blue to-cyan-600 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h2 className="text-3xl font-bold flex items-center gap-3">
                    <Sparkles className="w-8 h-8" />
                    Diagnostic TI Professionnel
                  </h2>
                  {/* Badge AI - Disponible dans AI Assistant */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                    title="Disponible dans AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <p className="text-white/90">Évaluations conformes COBIT, ITIL, NIST, ISO</p>
                {lastAnalysisDate && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-white/80">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs - 6 onglets */}
            <div className="grid grid-cols-6 gap-2 mt-6">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'overview'
                    ? 'bg-white text-bz-blue shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <FileText className="w-5 h-5" />
                <span className="text-xs">Vue d'Ensemble</span>
              </button>
              <button
                onClick={() => setActiveTab('methodology')}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'methodology'
                    ? 'bg-white text-bz-blue shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <Award className="w-5 h-5" />
                <span className="text-xs">Méthodologie</span>
              </button>
              <button
                onClick={() => setActiveTab('roi')}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'roi'
                    ? 'bg-white text-bz-blue shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <TrendingUp className="w-5 h-5" />
                <span className="text-xs">ROI & Cas Clients</span>
              </button>
              <button
                onClick={() => setActiveTab('whybz')}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'whybz'
                    ? 'bg-white text-bz-blue shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <Shield className="w-5 h-5" />
                <span className="text-xs">Pourquoi BZ?</span>
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'pricing'
                    ? 'bg-white text-bz-blue shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <DollarSign className="w-5 h-5" />
                <span className="text-xs">Pricing</span>
              </button>
              <button
                onClick={() => setActiveTab('start')}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'start'
                    ? 'bg-white text-bz-blue shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <Sparkles className="w-5 h-5" />
                <span className="text-xs">Démarrer</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <AnimatePresence mode="wait">
              {activeTab === 'overview' && (
                <OverviewTab key="overview" />
              )}
              {activeTab === 'methodology' && (
                <MethodologyTab key="methodology" />
              )}
              {activeTab === 'roi' && (
                <ROITab key="roi" employeeCount={employeeCount} assetCount={assetCount} />
              )}
              {activeTab === 'whybz' && (
                <WhyBZTab key="whybz" />
              )}
              {activeTab === 'pricing' && (
                <PricingTab
                  key="pricing"
                  documentType={documentType}
                  setDocumentType={setDocumentType}
                  qualityLevel={qualityLevel}
                  setQualityLevel={setQualityLevel}
                  employeeCount={employeeCount}
                  setEmployeeCount={setEmployeeCount}
                  assetCount={assetCount}
                  setAssetCount={setAssetCount}
                  calculatePrice={calculatePrice}
                  getTotalPrice={getTotalPrice}
                />
              )}
              {activeTab === 'start' && (
                <StartTab key="start" onStartQuestionnaire={onStartQuestionnaire} />
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ============================================
// TAB 1: VUE D'ENSEMBLE
// ============================================
function OverviewTab() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      {/* Introduction pédagogique */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
        <h3 className="text-xl font-bold text-blue-900 mb-3">
          📚 Comprendre nos diagnostics TI (expliqué simplement)
        </h3>
        <p className="text-blue-800 leading-relaxed">
          Imaginez votre infrastructure TI comme une maison. Un <strong>Bilan de Santé TI</strong> vérifie 
          si les fondations sont solides, si la plomberie fonctionne, si l'électricité est aux normes. 
          Un <strong>Audit de Sécurité</strong> vérifie si les portes sont bien verrouillées, si l'alarme 
          fonctionne, et s'il y a des fenêtres ouvertes que des voleurs pourraient utiliser.
        </p>
      </div>

      {/* Les 2 documents */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Audit de Sécurité */}
        <div className="border-2 border-red-200 rounded-xl p-6 bg-gradient-to-br from-red-50 to-orange-50">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-red-500 text-white p-3 rounded-lg">
              <Shield className="w-8 h-8" />
            </div>
            <div>
              <h4 className="text-2xl font-bold text-red-900">Audit de Sécurité</h4>
              <p className="text-red-700 text-sm">Protection contre les cyberattaques</p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <h5 className="font-bold text-red-900">🎯 À quoi ça sert?</h5>
            <ul className="space-y-2 text-sm text-red-800">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span>Identifier les <strong>failles de sécurité</strong> avant qu'un pirate les exploite</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span>Protéger vos <strong>données confidentielles</strong> (clients, finances, propriété intellectuelle)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span>Respecter les <strong>lois</strong> (Loi 25, ISO 27001, etc.)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span>Éviter les <strong>ransomwares</strong> qui bloquent vos systèmes</span>
              </li>
            </ul>
          </div>

          <div className="bg-red-100 border border-red-300 rounded-lg p-4">
            <p className="text-xs text-red-900">
              <strong>💡 Saviez-vous?</strong> Le coût moyen d'une cyberattaque pour une PME est de <strong>150 000$ à 500 000$</strong>. 
              Un audit préventif coûte 10 à 20 fois moins cher!
            </p>
          </div>
        </div>

        {/* Bilan de Santé TI */}
        <div className="border-2 border-blue-200 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-cyan-50">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-500 text-white p-3 rounded-lg">
              <TrendingUp className="w-8 h-8" />
            </div>
            <div>
              <h4 className="text-2xl font-bold text-blue-900">Bilan de Santé TI</h4>
              <p className="text-blue-700 text-sm">Optimisation et gouvernance</p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <h5 className="font-bold text-blue-900">🎯 À quoi ça sert?</h5>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Mesurer votre <strong>maturité TI</strong> vs les meilleures pratiques</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Identifier les <strong>gaspillages</strong> (licences inutiles, processus lents)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Prioriser les <strong>investissements TI</strong> avec ROI clair</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Aligner la TI avec vos <strong>objectifs d'affaires</strong></span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-100 border border-blue-300 rounded-lg p-4">
            <p className="text-xs text-blue-900">
              <strong>💡 Saviez-vous?</strong> Les PME avec une gouvernance TI mature ont <strong>35% plus de croissance</strong> 
              et <strong>40% moins de pannes</strong> que les autres!
            </p>
          </div>
        </div>
      </div>

      {/* Comment fonctionne le pricing */}
      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
        <h3 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-2">
          <Calculator className="w-6 h-6" />
          💰 Comment est calculé le prix? (Transparence totale!)
        </h3>
        
        <div className="space-y-4">
          <p className="text-amber-900">
            Nos prix sont <strong>justes et personnalisés</strong> selon la taille et complexité de votre organisation:
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-amber-600" />
                <h5 className="font-bold text-amber-900">Nombre d'employés</h5>
              </div>
              <p className="text-sm text-amber-800">
                Plus vous avez d'employés, plus il y a d'utilisateurs, de comptes, de permissions à analyser.
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Server className="w-5 h-5 text-amber-600" />
                <h5 className="font-bold text-amber-900">Nombre d'actifs TI</h5>
              </div>
              <p className="text-sm text-amber-800">
                Serveurs, équipements réseau, applications critiques. Plus il y en a, plus l'analyse est détaillée.
              </p>
            </div>
          </div>

          <div className="bg-amber-100 rounded-lg p-4">
            <p className="text-sm text-amber-900">
              <strong>Exemple concret:</strong> Une entreprise de <strong>50 employés</strong> avec <strong>15 actifs TI</strong> 
              paiera environ <strong>10 500$</strong> pour un Audit de Sécurité Professionnel. 
              Une entreprise de <strong>200 employés</strong> avec <strong>50 actifs</strong> paiera environ <strong>22 000$</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Cas clients & ROI */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6">
        <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center gap-2">
          💰 ROI & Bénéfices Concrets
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg p-5 border-2 border-green-200">
            <div className="text-3xl mb-3">📈</div>
            <h4 className="font-bold text-green-900 mb-2">Cas Client: Cabinet d'avocats</h4>
            <p className="text-sm text-gray-700 mb-3">
              <strong>50 employés, secteur hautement réglementé</strong>
            </p>
            
            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Investissement:</span>
                <span className="font-bold">18 700$</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Économies 1ère année:</span>
                <span className="font-bold text-green-600">45 000$</span>
              </div>
            </div>
            
            <div className="bg-green-100 rounded p-3 text-xs space-y-1">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Élimination licences inutiles: 12 000$/an</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Réduction incidents (-60%): 18 000$/an</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Conformité Loi 25 (évite amendes): 15 000$</span>
              </div>
            </div>
            
            <p className="mt-3 text-xs font-bold text-green-900">
              ✨ ROI: 241% en 12 mois
            </p>
          </div>

          <div className="bg-white rounded-lg p-5 border-2 border-green-200">
            <div className="text-3xl mb-3">🏭</div>
            <h4 className="font-bold text-green-900 mb-2">Cas Client: Manufacturier</h4>
            <p className="text-sm text-gray-700 mb-3">
              <strong>120 employés, infrastruct vieillissante</strong>
            </p>
            
            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Investissement:</span>
                <span className="font-bold">28 500$</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Économies 1ère année:</span>
                <span className="font-bold text-green-600">85 000$</span>
              </div>
            </div>
            
            <div className="bg-green-100 rounded p-3 text-xs space-y-1">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Éviter ransomware (estimé): 50 000$</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Optimisation cloud: 22 000$/an</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Productivité (+15%): 13 000$/an</span>
              </div>
            </div>
            
            <p className="mt-3 text-xs font-bold text-green-900">
              ✨ ROI: 298% en 12 mois
            </p>
          </div>
        </div>
      </div>

      {/* Processus étape par étape */}
      <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-6">
        <h3 className="text-xl font-bold text-purple-900 mb-4">
          🛣️ Notre Processus (Transparent & Structuré)
        </h3>
        
        <div className="space-y-4">
          {[
            {
              step: '1',
              title: 'Questionnaire Holistique',
              duration: '60-90 min',
              desc: 'Vous remplissez notre questionnaire de 10 phases qui couvre TOUS les aspects de votre TI.',
              color: 'blue'
            },
            {
              step: '2',
              title: 'Analyse par nos Experts',
              duration: '3-20 jours',
              desc: 'Nos consultants certifiés COBIT/ITIL/NIST analysent vos réponses selon les standards internationaux.',
              color: 'purple'
            },
            {
              step: '3',
              title: 'Génération Documents',
              duration: '1-2 jours',
              desc: 'Création de vos rapports personnalisés (20 à 80 pages selon le niveau choisi).',
              color: 'pink'
            },
            {
              step: '4',
              title: 'Présentation Résultats',
              duration: '2-3 heures',
              desc: 'Rencontre avec vous pour expliquer les findings, recommandations et roadmap.',
              color: 'green'
            },
            {
              step: '5',
              title: 'Plan d\'Action & Suivi',
              duration: 'Continu',
              desc: 'Nous vous accompagnons dans l\'exécution des recommandations prioritaires.',
              color: 'orange'
            }
          ].map((item, idx) => (
            <div key={idx} className="flex gap-4 items-start">
              <div className={`flex-shrink-0 w-12 h-12 rounded-full bg-${item.color}-500 text-white font-bold text-xl flex items-center justify-center shadow-lg`}>
                {item.step}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-bold text-gray-900">{item.title}</h4>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{item.duration}</span>
                </div>
                <p className="text-sm text-gray-700">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Pédagogique */}
      <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          ❓ Questions Fréquentes (Pour mieux comprendre)
        </h3>
        
        <div className="space-y-4">
          {[
            {
              q: 'Quelle est la différence entre un Audit de Sécurité et un Bilan de Santé TI?',
              a: 'L\'Audit se concentre sur la SÉCURITÉ (cyberattaques, conformité). Le Bilan évalue la MATURITÉ GLOBALE (gouvernance, processus, performance). Les deux sont complémentaires!'
            },
            {
              q: 'Est-ce que je dois fournir des mots de passe ou accès à mes systèmes?',
              a: 'NON! Le questionnaire ne demande AUCUNE information sensible (pas de mots de passe, pas d\'IPs). Seulement des infos sur votre structure, processus et architecture.'
            },
            {
              q: 'Combien de temps les résultats restent-ils valides?',
              a: 'Un diagnostic est valide 12-18 mois. Après, votre TI évolue (nouveaux systèmes, employés, menaces). Nous recommandons un diagnostic annuel.'
            },
            {
              q: 'Est-ce adapté aux petites entreprises (moins de 50 employés)?',
              a: 'OUI! Nos 3 niveaux s\'adaptent. Le niveau Essentiel est parfait pour PME 10-50 employés avec budget limité. Qualité professionnelle garantie!'
            },
            {
              q: 'Que se passe-t-il si je ne comprends pas les questions techniques?',
              a: 'AUCUN PROBLÈME! Toutes les questions ont des exemples et aides contextuelles. Vous pouvez aussi laisser vide - nous contactons votre équipe TI au besoin.'
            },
            {
              q: 'Les documents sont-ils utilisables pour une certification (ISO, SOC2)?',
              a: 'ABSOLUMENT! Nos rapports sont conformes aux standards internationaux. Ils servent de GAP ANALYSIS pour préparer vos certifications futures.'
            }
          ].map((item, idx) => (
            <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
              <h5 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                <span className="text-blue-600 flex-shrink-0">Q:</span>
                <span>{item.q}</span>
              </h5>
              <p className="text-sm text-gray-700 ml-6">{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Comparaison avec la compétition */}
      <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          ⚡ Pourquoi BZ vs d'autres consultants?
        </h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
            <h5 className="font-bold text-gray-500 mb-3 text-center">Consultants Traditionnels</h5>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Rapports génériques (copier-coller)</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Délais longs (4-8 semaines)</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Prix opaques (surprise finale)</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Recommandations vagues</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 border-4 border-blue-500 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full">
              ⭐ C'EST NOUS!
            </div>
            <h5 className="font-bold text-blue-900 mb-3 text-center mt-2">Bergeron Zaccheo (BZ)</h5>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-900 font-medium">100% personnalisé (vos données)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-900 font-medium">Rapide (3-20 jours max)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-900 font-medium">Prix transparents (calculateur)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-900 font-medium">Actions avec coûts/ROI/délais</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
            <h5 className="font-bold text-gray-500 mb-3 text-center">Outils Automatisés</h5>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Aucun contexte d'affaires</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Rapports superficiels (10 pages)</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Aucun accompagnement humain</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Pas de suivi post-livraison</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Garanties & Engagement */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl p-6">
        <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <Award className="w-7 h-7" />
          🏆 Nos Garanties (On est sérieux!)
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white/10 backdrop-blur rounded-lg p-4">
            <h5 className="font-bold mb-2">✅ Qualité Garantie</h5>
            <p className="text-sm text-white/90">
              Si vous n'êtes pas 100% satisfait, on refait le rapport GRATUITEMENT ou remboursement complet.
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-4">
            <h5 className="font-bold mb-2">✅ Confidentialité Absolue</h5>
            <p className="text-sm text-white/90">
              NDA signé, données chiffrées, respect des normes ISO 27001. Vos infos ne sortent JAMAIS.
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-4">
            <h5 className="font-bold mb-2">✅ Délais Respectés</h5>
            <p className="text-sm text-white/90">
              Si on dépasse les délais annoncés, 10% de remise par jour de retard (max 50%).
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-4">
            <h5 className="font-bold mb-2">✅ Support Inclus (30 jours)</h5>
            <p className="text-sm text-white/90">
              Questions après livraison? On répond par email/téléphone sans frais additionnels pendant 30 jours.
            </p>
          </div>
        </div>
      </div>

      {/* Call to action */}
      <div className="flex gap-4 justify-center pt-4">
        <button className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105">
          <Shield className="w-5 h-5" />
          Voir les options & prix →
        </button>
      </div>
    </motion.div>
  );
}

// ============================================
// TAB 2: MÉTHODOLOGIE & PROCESSUS
// ============================================
function MethodologyTab() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      {/* Introduction */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-300 rounded-xl p-6">
        <h3 className="text-2xl font-bold text-purple-900 mb-3 flex items-center gap-2">
          <Award className="w-7 h-7" />
          🔬 Notre Méthodologie Éprouvée
        </h3>
        <p className="text-gray-700 leading-relaxed">
          Notre approche combine <strong>intelligence artificielle avancée</strong>, <strong>expertise humaine senior</strong>, 
          et <strong>frameworks internationaux reconnus</strong> pour vous livrer un diagnostic de qualité institutionnelle.
        </p>
      </div>

      {/* Processus en 5 phases */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          📋 Processus Détaillé (5 Phases)
        </h3>
        
        <div className="space-y-4">
          {/* Phase 1 */}
          <div className="bg-white border-2 border-blue-200 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg">
                1
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Collecte Holistique</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Questionnaire détaillé de 10 phases (60-90 min) couvrant infrastructure, sécurité, gouvernance, équipe, financier, et vision stratégique.
                </p>
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Sauvegarde automatique (LocalStorage)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Inventaires détaillés (serveurs, apps, contrats)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Entrevues avec parties prenantes (Pro/Expert)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Visite terrain (Expert uniquement)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 2 */}
          <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg">
                2
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Analyse Automatisée (IA)</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Traitement par IA avancée (GPT-4) avec prompts structurés selon COBIT 2019, ITIL 4, NIST CSF 2.0, ISO 27001:2022.
                </p>
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Scoring de maturité 1-5 (COBIT/CMMi)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Détection écarts vs normes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Génération recommandations initiales</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Spider charts & visualisations</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 3 */}
          <div className="bg-white border-2 border-green-200 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg">
                3
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Validation Experte</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Révision par consultants certifiés (CISSP, CISM, ITIL Expert, COBIT Assessor) pour valider analyses IA et enrichir recommandations.
                </p>
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Vérification conformité normes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Ajustement contexte sectoriel</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Priorisation ROI/effort</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Revue pairs (Expert)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 4 */}
          <div className="bg-white border-2 border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg">
                4
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Rédaction Professionnelle</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Assemblage document final (15-80 pages selon niveau) avec sommaire exécutif, analyses détaillées, roadmap, et annexes.
                </p>
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Formatage professionnel (branding BZ)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Génération charts/graphiques</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Table des matières interactive</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Export PDF + PowerPoint</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 5 */}
          <div className="bg-white border-2 border-red-200 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg">
                5
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Présentation & Suivi</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Session de présentation avec vos parties prenantes (30 min à 3h selon niveau), Q&A, et support post-livraison 30 jours.
                </p>
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Présentation PowerPoint personnalisée</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Session Q&A approfondie</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Support clarifications 30 jours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Suivi implémentation (optionnel)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Frameworks & Outils */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-gray-300">
        <h3 className="text-xl font-bold text-gray-900 mb-4">🛠️ Frameworks & Outils Utilisés</h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          {/* Gouvernance */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="font-bold text-sm text-blue-700 mb-3">Gouvernance TI</h4>
            <ul className="space-y-2 text-xs text-gray-700">
              <li className="flex items-center gap-2">
                <Award className="w-3 h-3 text-blue-600" />
                <span><strong>COBIT 2019</strong> (40 processus)</span>
              </li>
              <li className="flex items-center gap-2">
                <Award className="w-3 h-3 text-blue-600" />
                <span><strong>ITIL 4</strong> (34 pratiques)</span>
              </li>
              <li className="flex items-center gap-2">
                <Award className="w-3 h-3 text-blue-600" />
                <span><strong>ISO/IEC 20000-1:2018</strong></span>
              </li>
              <li className="flex items-center gap-2">
                <Award className="w-3 h-3 text-blue-600" />
                <span><strong>CMMi v2.0</strong></span>
              </li>
            </ul>
          </div>

          {/* Sécurité */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="font-bold text-sm text-red-700 mb-3">Cybersécurité</h4>
            <ul className="space-y-2 text-xs text-gray-700">
              <li className="flex items-center gap-2">
                <Shield className="w-3 h-3 text-red-600" />
                <span><strong>NIST CSF 2.0</strong> (6 fonctions)</span>
              </li>
              <li className="flex items-center gap-2">
                <Shield className="w-3 h-3 text-red-600" />
                <span><strong>ISO 27001:2022</strong> (93 contrôles)</span>
              </li>
              <li className="flex items-center gap-2">
                <Shield className="w-3 h-3 text-red-600" />
                <span><strong>CIS Controls v8</strong></span>
              </li>
              <li className="flex items-center gap-2">
                <Shield className="w-3 h-3 text-red-600" />
                <span><strong>OWASP Top 10</strong></span>
              </li>
            </ul>
          </div>

          {/* Outils Tech */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="font-bold text-sm text-purple-700 mb-3">Technologies</h4>
            <ul className="space-y-2 text-xs text-gray-700">
              <li className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-purple-600" />
                <span><strong>OpenAI GPT-4</strong> (analyse)</span>
              </li>
              <li className="flex items-center gap-2">
                <Server className="w-3 h-3 text-purple-600" />
                <span><strong>React + TypeScript</strong></span>
              </li>
              <li className="flex items-center gap-2">
                <Package className="w-3 h-3 text-purple-600" />
                <span><strong>jsPDF</strong> (génération PDF)</span>
              </li>
              <li className="flex items-center gap-2">
                <Calculator className="w-3 h-3 text-purple-600" />
                <span><strong>Chart.js</strong> (visualisations)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Équipe */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">👥 Qui Intervient Sur Votre Projet?</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          {/* Essentiel */}
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-5">
            <h4 className="font-bold text-md text-green-700 mb-3">🥉 Niveau Essentiel</h4>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <Users className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">1 Consultant junior/intermédiaire</p>
                  <p className="text-xs text-gray-600">Révision par senior (validation qualité)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pro */}
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-5">
            <h4 className="font-bold text-md text-blue-700 mb-3">🥈 Niveau Professionnel</h4>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <Users className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">1 Consultant senior certifié</p>
                  <p className="text-xs text-gray-600">+ Révision consultant principal</p>
                  <p className="text-xs text-gray-600 mt-1">Certifications: CISSP, CISM, ITIL Expert, COBIT</p>
                </div>
              </div>
            </div>
          </div>

          {/* Expert */}
          <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-5 md:col-span-2">
            <h4 className="font-bold text-md text-purple-700 mb-3">🥇 Niveau Expert</h4>
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <Users className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold">Équipe dédiée multi-expertise:</p>
                  <ul className="text-xs text-gray-600 mt-2 space-y-1 ml-4">
                    <li>• 1 Consultant principal (Chef de projet)</li>
                    <li>• 1 Expert cybersécurité (CISSP, CEH)</li>
                    <li>• 1 Architecte infrastructure (TOGAF, AWS/Azure)</li>
                    <li>• 1 Spécialiste conformité (ISO 27001 Lead Auditor)</li>
                    <li>• Revue pairs par partenaire BZ (20+ ans expérience)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gouvernance */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-xl p-6">
        <h3 className="text-xl font-bold text-indigo-900 mb-4">⚖️ Gouvernance & Qualité</h3>
        
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-indigo-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
              ✓
            </div>
            <div>
              <p className="font-bold text-gray-900">Revue Qualité Systématique</p>
              <p className="text-xs text-gray-600 mt-1">Chaque document passe par 2 niveaux de validation avant livraison</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-indigo-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
              ✓
            </div>
            <div>
              <p className="font-bold text-gray-900">Conformité Normes Internationales</p>
              <p className="text-xs text-gray-600 mt-1">Alignement strict COBIT, ITIL, NIST, ISO vérifié par auditeurs certifiés</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-indigo-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
              ✓
            </div>
            <div>
              <p className="font-bold text-gray-900">Versioning & Traçabilité</p>
              <p className="text-xs text-gray-600 mt-1">Historique complet des analyses, versions, et validations</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-indigo-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
              ✓
            </div>
            <div>
              <p className="font-bold text-gray-900">Audit Externe Annuel</p>
              <p className="text-xs text-gray-600 mt-1">Notre méthodologie est auditée annuellement par cabinet externe</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// TAB 3: ROI & CAS CLIENTS
// ============================================
function ROITab({ employeeCount, assetCount }: { employeeCount: number; assetCount: number }) {
  // Calculs ROI simulés basés sur benchmarks industrie
  const avgCyberIncidentCost = employeeCount * 8500; // $8,500 par employé (benchmark IBM)
  const avgDowntimeCostPerYear = assetCount * 12000; // $12,000 par actif/an
  const avgComplianceFines = employeeCount >= 100 ? 250000 : 50000;
  
  const essentielPrice = 2500 + (employeeCount * 25) + (assetCount * 40);
  const proPrice = 7000 + (employeeCount * 45) + (assetCount * 70);
  const expertPrice = 15000 + (employeeCount * 70) + (assetCount * 100);
  
  const potentialSavingsYear1 = avgDowntimeCostPerYear * 0.3; // 30% réduction downtime
  const roiEssentiel = ((potentialSavingsYear1 - essentielPrice) / essentielPrice * 100).toFixed(0);
  const roiPro = ((potentialSavingsYear1 - proPrice) / proPrice * 100).toFixed(0);
  const roiExpert = ((potentialSavingsYear1 - expertPrice) / expertPrice * 100).toFixed(0);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      {/* Calculateur ROI */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-6">
        <h3 className="text-2xl font-bold text-green-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-7 h-7" />
          💰 Calculateur ROI (Retour Sur Investissement)
        </h3>
        
        <div className="bg-white rounded-lg p-5 border-2 border-green-300">
          <p className="text-sm text-gray-700 mb-4">
            Pour une organisation de <strong>{employeeCount} employés</strong> avec <strong>{assetCount} actifs TI</strong>:
          </p>
          
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-xs text-red-600 font-bold mb-1">Coût Incident Cyber (moy.)</p>
              <p className="text-2xl font-bold text-red-700">{avgCyberIncidentCost.toLocaleString('fr-CA')} $</p>
              <p className="text-xs text-gray-500 mt-1">Source: IBM Cost of Data Breach 2024</p>
            </div>
            
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <p className="text-xs text-orange-600 font-bold mb-1">Downtime annuel (moy.)</p>
              <p className="text-2xl font-bold text-orange-700">{avgDowntimeCostPerYear.toLocaleString('fr-CA')} $</p>
              <p className="text-xs text-gray-500 mt-1">Pannes, lenteurs, incidents</p>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <p className="text-xs text-purple-600 font-bold mb-1">Non-conformité (risque)</p>
              <p className="text-2xl font-bold text-purple-700">{avgComplianceFines.toLocaleString('fr-CA')} $</p>
              <p className="text-xs text-gray-500 mt-1">Amendes Loi 25, PIPEDA, etc.</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-5 border-2 border-green-400">
            <p className="text-sm font-bold text-green-900 mb-2">💡 Économies Potentielles Année 1 (estimation conservative):</p>
            <p className="text-3xl font-bold text-green-700">{potentialSavingsYear1.toLocaleString('fr-CA')} $</p>
            <p className="text-xs text-gray-600 mt-2">
              Basé sur réduction de 30% du downtime + prévention d'1 incident cyber sur 3 ans
            </p>
          </div>
        </div>
        
        {/* ROI par niveau */}
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white rounded-lg p-4 border-2 border-green-300">
            <h4 className="font-bold text-green-700 mb-2">🥉 Essentiel</h4>
            <p className="text-sm text-gray-600 mb-2">Investissement: {essentielPrice.toLocaleString('fr-CA')} $</p>
            <p className="text-2xl font-bold text-green-600">ROI: {roiEssentiel}%</p>
            <p className="text-xs text-gray-500 mt-1">Rentable en ~{Math.round(essentielPrice / (potentialSavingsYear1 / 12))} mois</p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 border-2 border-blue-400 shadow-lg">
            <h4 className="font-bold text-blue-700 mb-2">🥈 Professionnel ⭐</h4>
            <p className="text-sm text-gray-600 mb-2">Investissement: {proPrice.toLocaleString('fr-CA')} $</p>
            <p className="text-2xl font-bold text-blue-600">ROI: {roiPro}%</p>
            <p className="text-xs text-gray-500 mt-1">Rentable en ~{Math.round(proPrice / (potentialSavingsYear1 / 12))} mois</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border-2 border-purple-400">
            <h4 className="font-bold text-purple-700 mb-2">🥇 Expert</h4>
            <p className="text-sm text-gray-600 mb-2">Investissement: {expertPrice.toLocaleString('fr-CA')} $</p>
            <p className="text-2xl font-bold text-purple-600">ROI: {roiExpert}%</p>
            <p className="text-xs text-gray-500 mt-1">Rentable en ~{Math.round(expertPrice / (potentialSavingsYear1 / 12))} mois</p>
          </div>
        </div>
      </div>

      {/* Coût de l'inaction */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300 rounded-xl p-6">
        <h3 className="text-xl font-bold text-red-900 mb-4">
          ⚠️ Le Coût de NE PAS Agir (Inaction)
        </h3>
        
        <div className="space-y-3">
          <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
            <div className="flex items-start gap-3">
              <div className="text-3xl">💸</div>
              <div>
                <p className="font-bold text-gray-900">Dette Technique Croissante</p>
                <p className="text-sm text-gray-600 mt-1">
                  +15-20% par an de coûts de maintenance pour infrastructure obsolète
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 border-l-4 border-orange-500">
            <div className="flex items-start gap-3">
              <div className="text-3xl">🎯</div>
              <div>
                <p className="font-bold text-gray-900">Cyberattaques en Hausse</p>
                <p className="text-sm text-gray-600 mt-1">
                  68% des PME ciblées en 2024 (vs 50% en 2020). Rançongiciels en explosion.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 border-l-4 border-purple-500">
            <div className="flex items-start gap-3">
              <div className="text-3xl">⚖️</div>
              <div>
                <p className="font-bold text-gray-900">Conformité Obligatoire (Loi 25)</p>
                <p className="text-sm text-gray-600 mt-1">
                  Amendes jusqu'à 25M$ ou 4% du CA global. Entrée en vigueur complète 2024.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 border-l-4 border-gray-500">
            <div className="flex items-start gap-3">
              <div className="text-3xl">📉</div>
              <div>
                <p className="font-bold text-gray-900">Perte Compétitivité</p>
                <p className="text-sm text-gray-600 mt-1">
                  Productivité -30% avec TI sous-optimale. Recrutement difficile (talents fuient TI obsolète).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cas clients réels */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          📊 Cas Clients Réels (Résultats Mesurés)
        </h3>
        
        <div className="space-y-4">
          {/* Cas 1 */}
          <div className="bg-white border-2 border-blue-300 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-bold text-blue-900">Cabinet d'Avocats Montréal (75 employés)</h4>
                <p className="text-sm text-gray-600">Niveau: Professionnel | Secteur: Services juridiques</p>
              </div>
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-bold">
                ROI: 320%
              </div>
            </div>
            
            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-600 font-bold mb-1">Investissement</p>
                <p className="text-lg font-bold text-blue-900">11 250 $</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-green-600 font-bold mb-1">Économies An 1</p>
                <p className="text-lg font-bold text-green-700">36 000 $</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <p className="text-xs text-purple-600 font-bold mb-1">Durée Projet</p>
                <p className="text-lg font-bold text-purple-900">5 semaines</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-xs text-amber-600 font-bold mb-1">ISO 27001</p>
                <p className="text-lg font-bold text-amber-900">18 mois</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-bold text-gray-900 mb-2">💡 Économies Détaillées:</p>
              <ul className="text-xs text-gray-700 space-y-1 ml-4">
                <li>• Migration cloud AWS: -18 000$/an serveurs physiques</li>
                <li>• Automatisation backups: -8 000$/an MSP externe</li>
                <li>• Politique mots de passe + MFA: évité incident phishing (estimé 45 000$)</li>
                <li>• ISO 27001 obtenue = +12 contrats gouv. (285 000$ revenus additionnels)</li>
              </ul>
            </div>
          </div>

          {/* Cas 2 */}
          <div className="bg-white border-2 border-green-300 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-bold text-green-900">Manufacturier Québec (220 employés)</h4>
                <p className="text-sm text-gray-600">Niveau: Expert | Secteur: Fabrication industrielle</p>
              </div>
              <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                ROI: 580%
              </div>
            </div>
            
            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-green-600 font-bold mb-1">Investissement</p>
                <p className="text-lg font-bold text-green-900">38 500 $</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-xs text-emerald-600 font-bold mb-1">Économies An 1</p>
                <p className="text-lg font-bold text-emerald-700">223 000 $</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <p className="text-xs text-purple-600 font-bold mb-1">Durée Projet</p>
                <p className="text-lg font-bold text-purple-900">11 semaines</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-xs text-red-600 font-bold mb-1">Incidents Évités</p>
                <p className="text-lg font-bold text-red-900">2 critiques</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-bold text-gray-900 mb-2">💡 Économies Détaillées:</p>
              <ul className="text-xs text-gray-700 space-y-1 ml-4">
                <li>• Optimisation réseau industriel: -85 000$/an downtime production</li>
                <li>• Segmentation OT/IT: évité ransomware sur chaîne (estimé 1,2M$)</li>
                <li>• Plan continuité: réduction 70% temps reprise après panne</li>
                <li>• Conformité IEC 62443: contrat 4,5M$ avec client pharmaceutique</li>
                <li>• Réorganisation département TI: -3 postes redondants (138 000$/an)</li>
              </ul>
            </div>
          </div>

          {/* Cas 3 */}
          <div className="bg-white border-2 border-purple-300 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-bold text-purple-900">Clinique Médicale (12 employés)</h4>
                <p className="text-sm text-gray-600">Niveau: Essentiel | Secteur: Santé</p>
              </div>
              <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-bold">
                ROI: 180%
              </div>
            </div>
            
            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <p className="text-xs text-purple-600 font-bold mb-1">Investissement</p>
                <p className="text-lg font-bold text-purple-900">3 100 $</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-green-600 font-bold mb-1">Économies An 1</p>
                <p className="text-lg font-bold text-green-700">8 600 $</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-600 font-bold mb-1">Durée Projet</p>
                <p className="text-lg font-bold text-blue-900">2 semaines</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-xs text-amber-600 font-bold mb-1">Conformité</p>
                <p className="text-lg font-bold text-amber-900">PIPEDA ✓</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-bold text-gray-900 mb-2">💡 Économies Détaillées:</p>
              <ul className="text-xs text-gray-700 space-y-1 ml-4">
                <li>• Migration Office 365: -2 800$/an licences redondantes</li>
                <li>• Sauvegarde cloud dossiers patients: -1 500$/an bandes physiques</li>
                <li>• Politique BYOD + MDM: évité perte laptop (estimé 8 500$ amende PIPEDA)</li>
                <li>• Formation personnel cybersécurité: -80% incidents phishing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques industrie */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-xl p-6">
        <h3 className="text-xl font-bold text-indigo-900 mb-4">
          📈 Statistiques Industrie (Benchmarks 2024)
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm font-bold text-gray-900 mb-2">💰 ROI Moyen Diagnostic TI</p>
            <p className="text-3xl font-bold text-indigo-600 mb-2">250-400%</p>
            <p className="text-xs text-gray-600">Sur 12-18 mois (source: Gartner 2024)</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm font-bold text-gray-900 mb-2">⏱️ Temps Récupération</p>
            <p className="text-3xl font-bold text-blue-600 mb-2">4-8 mois</p>
            <p className="text-xs text-gray-600">Retour sur investissement complet</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm font-bold text-gray-900 mb-2">📊 Productivité TI</p>
            <p className="text-3xl font-bold text-green-600 mb-2">+35%</p>
            <p className="text-xs text-gray-600">Amélioration après implémentation recommandations</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm font-bold text-gray-900 mb-2">🔒 Réduction Incidents</p>
            <p className="text-3xl font-bold text-red-600 mb-2">-60%</p>
            <p className="text-xs text-gray-600">Incidents sécurité année 1 post-audit</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// TAB 4: POURQUOI BZ?
// ============================================
function WhyBZTab() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      {/* Hero Why BZ */}
      <div className="bg-gradient-to-br from-bz-blue/10 to-purple-100 border-2 border-bz-blue rounded-xl p-6">
        <h3 className="text-2xl font-bold text-bz-blue mb-3 flex items-center gap-2">
          <Shield className="w-7 h-7" />
          🏆 Pourquoi Choisir BZ Pour Votre Diagnostic TI?
        </h3>
        <p className="text-gray-700 leading-relaxed text-lg">
          Parce que vous méritez <strong>l'excellence</strong>, <strong>la transparence</strong>, 
          et des <strong>résultats mesurables</strong>. Pas de bullshit, juste de la valeur.
        </p>
      </div>

      {/* Comparaison avec concurrence */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          ⚖️ BZ vs Autres Solutions (Comparaison Honnête)
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
            <thead className="bg-gradient-to-r from-bz-blue to-purple-600 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-bold">Critère</th>
                <th className="px-4 py-3 text-center text-sm font-bold">🏆 BZ</th>
                <th className="px-4 py-3 text-center text-sm font-bold">Big 4<br/>(Deloitte, PwC)</th>
                <th className="px-4 py-3 text-center text-sm font-bold">Outils<br/>Automatisés</th>
                <th className="px-4 py-3 text-center text-sm font-bold">Consultants<br/>Indépendants</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {/* Prix */}
              <tr className="border-b border-gray-200">
                <td className="px-4 py-3 font-bold text-gray-900">💰 Prix (Audit Pro)</td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">8 000 $</span>
                  <br/><span className="text-xs text-green-600">✓ Transparent</span>
                </td>
                <td className="px-4 py-3 text-center bg-red-50">
                  <span className="text-red-700 font-bold">35 000 - 80 000 $</span>
                  <br/><span className="text-xs text-red-600">✗ Opaque</span>
                </td>
                <td className="px-4 py-3 text-center bg-yellow-50">
                  <span className="text-amber-700 font-bold">500 - 2 000 $</span>
                  <br/><span className="text-xs text-amber-600">⚠️ Limité</span>
                </td>
                <td className="px-4 py-3 text-center bg-gray-50">
                  <span className="text-gray-700 font-bold">5 000 - 25 000 $</span>
                  <br/><span className="text-xs text-gray-600">⚠️ Variable</span>
                </td>
              </tr>
              
              {/* Délais */}
              <tr className="border-b border-gray-200 bg-gray-50">
                <td className="px-4 py-3 font-bold text-gray-900">⏱️ Délais (Audit Pro)</td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">4-6 semaines</span>
                  <br/><span className="text-xs text-green-600">✓ Garanti</span>
                </td>
                <td className="px-4 py-3 text-center bg-red-50">
                  <span className="text-red-700 font-bold">3-6 mois</span>
                  <br/><span className="text-xs text-red-600">✗ Lent</span>
                </td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">Immédiat - 1 semaine</span>
                  <br/><span className="text-xs text-green-600">✓ Rapide</span>
                </td>
                <td className="px-4 py-3 text-center bg-yellow-50">
                  <span className="text-amber-700 font-bold">2-8 semaines</span>
                  <br/><span className="text-xs text-amber-600">⚠️ Imprévisible</span>
                </td>
              </tr>
              
              {/* Qualité */}
              <tr className="border-b border-gray-200">
                <td className="px-4 py-3 font-bold text-gray-900">🎯 Qualité/Profondeur</td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">⭐⭐⭐⭐⭐</span>
                  <br/><span className="text-xs text-green-600">IA + Experts certifiés</span>
                </td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">⭐⭐⭐⭐⭐</span>
                  <br/><span className="text-xs text-green-600">Excellent (mais cher)</span>
                </td>
                <td className="px-4 py-3 text-center bg-red-50">
                  <span className="text-red-700 font-bold">⭐⭐</span>
                  <br/><span className="text-xs text-red-600">Superficiel</span>
                </td>
                <td className="px-4 py-3 text-center bg-yellow-50">
                  <span className="text-amber-700 font-bold">⭐⭐⭐</span>
                  <br/><span className="text-xs text-amber-600">Variable (1 personne)</span>
                </td>
              </tr>
              
              {/* Personnalisation */}
              <tr className="border-b border-gray-200 bg-gray-50">
                <td className="px-4 py-3 font-bold text-gray-900">🎨 Personnalisation</td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">100%</span>
                  <br/><span className="text-xs text-green-600">✓ Adapté à vous</span>
                </td>
                <td className="px-4 py-3 text-center bg-yellow-50">
                  <span className="text-amber-700 font-bold">70%</span>
                  <br/><span className="text-xs text-amber-600">Templates standardisés</span>
                </td>
                <td className="px-4 py-3 text-center bg-red-50">
                  <span className="text-red-700 font-bold">10%</span>
                  <br/><span className="text-xs text-red-600">✗ Générique</span>
                </td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">90%</span>
                  <br/><span className="text-xs text-green-600">✓ Personnalisé</span>
                </td>
              </tr>
              
              {/* Certifications */}
              <tr className="border-b border-gray-200">
                <td className="px-4 py-3 font-bold text-gray-900">🏅 Certifications</td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">CISSP, CISM, ITIL, COBIT</span>
                  <br/><span className="text-xs text-green-600">✓ Équipe certifiée</span>
                </td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">Oui (nombreuses)</span>
                  <br/><span className="text-xs text-green-600">✓ Très qualifiés</span>
                </td>
                <td className="px-4 py-3 text-center bg-red-50">
                  <span className="text-red-700 font-bold">N/A</span>
                  <br/><span className="text-xs text-red-600">✗ Aucune</span>
                </td>
                <td className="px-4 py-3 text-center bg-yellow-50">
                  <span className="text-amber-700 font-bold">Variable</span>
                  <br/><span className="text-xs text-amber-600">⚠️ Dépend consultant</span>
                </td>
              </tr>
              
              {/* Support */}
              <tr className="border-b border-gray-200 bg-gray-50">
                <td className="px-4 py-3 font-bold text-gray-900">🤝 Support Post-Livraison</td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">30 jours inclus</span>
                  <br/><span className="text-xs text-green-600">✓ Réactif (24-48h)</span>
                </td>
                <td className="px-4 py-3 text-center bg-red-50">
                  <span className="text-red-700 font-bold">Facturation horaire</span>
                  <br/><span className="text-xs text-red-600">✗ 300-500$/h</span>
                </td>
                <td className="px-4 py-3 text-center bg-red-50">
                  <span className="text-red-700 font-bold">Aucun</span>
                  <br/><span className="text-xs text-red-600">✗ Autonome</span>
                </td>
                <td className="px-4 py-3 text-center bg-yellow-50">
                  <span className="text-amber-700 font-bold">Variable</span>
                  <br/><span className="text-xs text-amber-600">⚠️ Dépend contrat</span>
                </td>
              </tr>
              
              {/* Actionnabilité */}
              <tr>
                <td className="px-4 py-3 font-bold text-gray-900">🚀 Recommandations Actionnables</td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">Très détaillées</span>
                  <br/><span className="text-xs text-green-600">✓ Coût/ROI/Timeline</span>
                </td>
                <td className="px-4 py-3 text-center bg-yellow-50">
                  <span className="text-amber-700 font-bold">Stratégiques</span>
                  <br/><span className="text-xs text-amber-600">⚠️ Haut niveau</span>
                </td>
                <td className="px-4 py-3 text-center bg-red-50">
                  <span className="text-red-700 font-bold">Génériques</span>
                  <br/><span className="text-xs text-red-600">✗ Peu exploitables</span>
                </td>
                <td className="px-4 py-3 text-center bg-green-50">
                  <span className="text-green-700 font-bold">Pratiques</span>
                  <br/><span className="text-xs text-green-600">✓ Expérience terrain</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Nos certifications */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-6">
        <h3 className="text-xl font-bold text-amber-900 mb-4">
          🏅 Certifications & Accréditations de l'Équipe BZ
        </h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          {/* Cybersécurité */}
          <div className="bg-white rounded-lg p-5 border-2 border-red-300">
            <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Cybersécurité
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span><strong>CISSP</strong> - Certified Information Systems Security Professional</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span><strong>CISM</strong> - Certified Information Security Manager</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span><strong>CEH</strong> - Certified Ethical Hacker</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span><strong>ISO 27001 Lead Auditor</strong></span>
              </li>
            </ul>
          </div>

          {/* Gouvernance */}
          <div className="bg-white rounded-lg p-5 border-2 border-blue-300">
            <h4 className="font-bold text-blue-700 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Gouvernance TI
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span><strong>COBIT Assessor</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span><strong>ITIL 4 Managing Professional</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span><strong>PMP</strong> - Project Management Professional</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span><strong>TOGAF 9 Certified</strong></span>
              </li>
            </ul>
          </div>

          {/* Cloud & Infrastructure */}
          <div className="bg-white rounded-lg p-5 border-2 border-purple-300">
            <h4 className="font-bold text-purple-700 mb-3 flex items-center gap-2">
              <Server className="w-5 h-5" />
              Cloud & Infra
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span><strong>AWS Solutions Architect Professional</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span><strong>Azure Administrator Associate</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span><strong>Google Cloud Professional</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span><strong>Kubernetes CKA</strong></span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Notre expérience */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          📊 Notre Expérience en Chiffres
        </h3>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 text-center">
            <p className="text-5xl font-bold mb-2">15+</p>
            <p className="text-sm">Années d'expérience moyenne</p>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6 text-center">
            <p className="text-5xl font-bold mb-2">200+</p>
            <p className="text-sm">Projets diagnostics complétés</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-6 text-center">
            <p className="text-5xl font-bold mb-2">98%</p>
            <p className="text-sm">Taux satisfaction client</p>
          </div>
          
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl p-6 text-center">
            <p className="text-5xl font-bold mb-2">12</p>
            <p className="text-sm">Secteurs d'activité maîtrisés</p>
          </div>
        </div>
      </div>

      {/* Nos engagements */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-6">
        <h3 className="text-xl font-bold text-green-900 mb-4">
          🤝 Nos Engagements (Garantis par Contrat)
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-5 border-l-4 border-green-500">
            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              1. Satisfaction 100% ou Remboursement
            </h4>
            <p className="text-sm text-gray-600">
              Si vous n'êtes pas satisfait du livrable final, nous le révisons gratuitement jusqu'à satisfaction complète. 
              Si toujours insatisfait après 2 révisions, nous vous remboursons intégralement.
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-5 border-l-4 border-blue-500">
            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              2. Délais Respectés ou Rabais
            </h4>
            <p className="text-sm text-gray-600">
              Nous respectons nos délais annoncés. Pour chaque jour de retard (hors force majeure), 
              vous recevez automatiquement 10% de rabais sur le prix total (max 50%).
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-5 border-l-4 border-purple-500">
            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-purple-600" />
              3. Confidentialité Absolue
            </h4>
            <p className="text-sm text-gray-600">
              NDA signé avant projet. Données chiffrées (AES-256). Destruction sécurisée 90 jours après livraison. 
              Aucune réutilisation de vos données pour training IA.
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-5 border-l-4 border-amber-500">
            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-amber-600" />
              4. Support Gratuit 30 Jours
            </h4>
            <p className="text-sm text-gray-600">
              Questions, clarifications, présentations additionnelles: nous sommes disponibles 30 jours post-livraison 
              sans frais supplémentaires (réponse sous 24-48h).
            </p>
          </div>
        </div>
      </div>

      {/* Secteurs d'expertise */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          🏢 Secteurs d'Expertise
        </h3>
        
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          {[
            '🏥 Santé (cliniques, hôpitaux)',
            '⚖️ Services juridiques (cabinets)',
            '🏦 Finance (coopératives, caisses)',
            '🏭 Manufacturier (PME industrielles)',
            '🏗️ Construction & Ingénierie',
            '🎓 Éducation (cégeps, universités)',
            '📱 Technologies (SaaS, logiciels)',
            '🛒 Commerce détail (multimagasins)',
            '🚚 Transport & Logistique',
            '🏛️ Gouvernement & Municipal',
            '🎨 Services professionnels',
            '⚡ Énergie & Utilities'
          ].map((sector, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-gray-700">{sector}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// TAB 6: DÉMARRER
// ============================================
function StartTab({ onStartQuestionnaire }: { onStartQuestionnaire: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      {/* CTA Principal */}
      <div className="bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500 text-white rounded-xl p-8 text-center">
        <h3 className="text-3xl font-bold mb-4">🚀 Prêt à Transformer Votre TI?</h3>
        <p className="text-xl mb-6 opacity-90">
          Démarrez votre diagnostic holistique maintenant. 60-90 minutes pour une analyse complète.
        </p>
        
        <button
          onClick={onStartQuestionnaire}
          className="flex items-center gap-3 px-12 py-6 bg-white text-purple-600 rounded-xl font-bold text-2xl shadow-2xl hover:shadow-3xl transition-all hover:scale-105 mx-auto"
        >
          <Sparkles className="w-8 h-8" />
          Démarrer le Questionnaire
        </button>
        
        <p className="text-sm mt-4 opacity-75">
          ✓ Sauvegarde automatique &nbsp;&nbsp; ✓ Aucun engagement &nbsp;&nbsp; ✓ 100% confidentiel
        </p>
      </div>

      {/* Ce qui va se passer */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          📋 Ce Qui Va Se Passer (Prochaines Étapes)
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-start gap-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-5">
            <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              1
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1">Questionnaire Holistique (60-90 min)</h4>
              <p className="text-sm text-gray-600">
                10 phases couvrant infrastructure, sécurité, gouvernance, équipe, financier, et vision. 
                Sauvegarde automatique à chaque étape.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 bg-purple-50 border-l-4 border-purple-500 rounded-r-lg p-5">
            <div className="w-10 h-10 bg-purple-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              2
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1">Choix du Niveau & Paiement</h4>
              <p className="text-sm text-gray-600">
                Sélectionnez votre niveau (Essentiel, Pro, Expert) selon vos besoins et budget. 
                Paiement sécurisé (50/50 ou comptant).
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg p-5">
            <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              3
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1">Analyse & Rédaction (2-12 semaines)</h4>
              <p className="text-sm text-gray-600">
                Notre équipe (IA + experts) analyse vos données, génère recommandations, et rédige votre document personnalisé.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-5">
            <div className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              4
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1">Livraison & Présentation</h4>
              <p className="text-sm text-gray-600">
                Réception document PDF + PowerPoint. Session de présentation avec vos parties prenantes. 
                Q&A approfondie.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-5">
            <div className="w-10 h-10 bg-indigo-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              5
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1">Implémentation & Suivi (Optionnel)</h4>
              <p className="text-sm text-gray-600">
                Support 30 jours inclus. Accompagnement implémentation disponible séparément si désiré.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ dernières questions */}
      <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-300">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          ❓ Dernières Questions Avant de Démarrer?
        </h3>
        
        <div className="space-y-3 text-sm">
          <details className="bg-white rounded-lg p-4 border border-gray-200">
            <summary className="font-bold text-gray-900 cursor-pointer">
              Est-ce que je peux sauvegarder et continuer plus tard?
            </summary>
            <p className="text-gray-600 mt-2 ml-4">
              Oui! Le questionnaire sauvegarde automatiquement vos réponses dans votre navigateur (LocalStorage). 
              Vous pouvez fermer et revenir quand vous voulez.
            </p>
          </details>
          
          <details className="bg-white rounded-lg p-4 border border-gray-200">
            <summary className="font-bold text-gray-900 cursor-pointer">
              Est-ce que je m'engage à acheter en commençant le questionnaire?
            </summary>
            <p className="text-gray-600 mt-2 ml-4">
              Non, absolument pas! Le questionnaire est gratuit et sans engagement. Vous verrez le prix exact à la fin 
              et déciderez si vous voulez procéder.
            </p>
          </details>
          
          <details className="bg-white rounded-lg p-4 border border-gray-200">
            <summary className="font-bold text-gray-900 cursor-pointer">
              Que se passe-t-il avec mes données sensibles?
            </summary>
            <p className="text-gray-600 mt-2 ml-4">
              Vos données restent locales jusqu'à ce que vous payiez et confirmiez le projet. NDA signé avant transmission. 
              Chiffrement AES-256. Destruction 90 jours post-livraison.
            </p>
          </details>
          
          <details className="bg-white rounded-lg p-4 border border-gray-200">
            <summary className="font-bold text-gray-900 cursor-pointer">
              Je ne connais pas toutes les réponses techniques, est-ce grave?
            </summary>
            <p className="text-gray-600 mt-2 ml-4">
              Pas de problème! Répondez au meilleur de vos connaissances. Vous pouvez écrire "Je ne sais pas" ou laisser vide. 
              Nous identifierons les zones à approfondir lors des entrevues (Pro/Expert).
            </p>
          </details>
        </div>
      </div>

      {/* CTA Final */}
      <div className="text-center">
        <button
          onClick={onStartQuestionnaire}
          className="flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold text-xl shadow-2xl hover:shadow-3xl transition-all hover:scale-105 mx-auto"
        >
          <Sparkles className="w-6 h-6" />
          C'est Parti! Démarrer Maintenant
        </button>
        
        <p className="text-sm text-gray-500 mt-4">
          🕐 Temps estimé: 60-90 minutes &nbsp;&nbsp;|&nbsp;&nbsp; 💾 Sauvegarde auto &nbsp;&nbsp;|&nbsp;&nbsp; 🔒 100% confidentiel
        </p>
      </div>
    </motion.div>
  );
}

// ============================================
// TAB 5: PRICING
// ============================================
function PricingTab({
  documentType,
  setDocumentType,
  qualityLevel,
  setQualityLevel,
  employeeCount,
  setEmployeeCount,
  assetCount,
  setAssetCount,
  calculatePrice,
  getTotalPrice
}: any) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Sélection type de document */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">1️⃣ Choisissez votre document</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => setDocumentType('audit')}
            className={`p-6 rounded-xl border-2 transition-all ${
              documentType === 'audit'
                ? 'border-red-500 bg-red-50 shadow-lg'
                : 'border-gray-200 hover:border-red-300'
            }`}
          >
            <Shield className={`w-12 h-12 mx-auto mb-3 ${documentType === 'audit' ? 'text-red-600' : 'text-gray-400'}`} />
            <h4 className="font-bold text-lg mb-2">Audit de Sécurité</h4>
            <p className="text-sm text-gray-600">Protection cyber & conformité</p>
          </button>

          <button
            onClick={() => setDocumentType('bilan')}
            className={`p-6 rounded-xl border-2 transition-all ${
              documentType === 'bilan'
                ? 'border-blue-500 bg-blue-50 shadow-lg'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <TrendingUp className={`w-12 h-12 mx-auto mb-3 ${documentType === 'bilan' ? 'text-blue-600' : 'text-gray-400'}`} />
            <h4 className="font-bold text-lg mb-2">Bilan de Santé TI</h4>
            <p className="text-sm text-gray-600">Maturité & gouvernance</p>
          </button>

          <button
            onClick={() => setDocumentType('package')}
            className={`p-6 rounded-xl border-2 transition-all relative ${
              documentType === 'package'
                ? 'border-purple-500 bg-purple-50 shadow-lg'
                : 'border-gray-200 hover:border-purple-300'
            }`}
          >
            <div className="absolute -top-3 -right-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              -15% 🎉
            </div>
            <Package className={`w-12 h-12 mx-auto mb-3 ${documentType === 'package' ? 'text-purple-600' : 'text-gray-400'}`} />
            <h4 className="font-bold text-lg mb-2">Package Complet</h4>
            <p className="text-sm text-gray-600">Audit + Bilan (économie 15%)</p>
          </button>
        </div>
      </div>

      {/* Calculateur */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calculator className="w-6 h-6" />
          2️⃣ Informations sur votre organisation
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4 inline mr-1" />
              Nombre d'employés
            </label>
            <input
              type="number"
              value={employeeCount}
              onChange={(e) => setEmployeeCount(parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg text-lg font-bold focus:ring-2 focus:ring-blue-500"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Server className="w-4 h-4 inline mr-1" />
              Nombre d'actifs TI (serveurs, équipements réseau, apps critiques)
            </label>
            <input
              type="number"
              value={assetCount}
              onChange={(e) => setAssetCount(parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg text-lg font-bold focus:ring-2 focus:ring-blue-500"
              min="1"
            />
          </div>
        </div>

        <div className="mt-4 bg-blue-100 border border-blue-300 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            💡 <strong>Aide:</strong> Les "actifs TI" incluent: serveurs (physiques + virtuels), 
            équipements réseau (switches, firewalls), applications critiques métier.
          </p>
        </div>
      </div>

      {/* Suggestion intelligente */}
      {employeeCount > 0 && assetCount > 0 && (
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-6">
          <h3 className="text-lg font-bold text-purple-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            💡 Notre Recommandation Pour Vous
          </h3>
          
          {(() => {
            const suggestedLevel = employeeCount <= 50 && assetCount <= 20 ? 'essentiel' 
              : employeeCount >= 150 || assetCount >= 50 ? 'expert' 
              : 'pro';
            
            const levelNames = { essentiel: 'Essentiel', pro: 'Professionnel', expert: 'Expert' };
            
            return (
              <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
                <p className="text-sm text-gray-800 mb-3">
                  Pour une organisation de <strong>{employeeCount} employés</strong> avec <strong>{assetCount} actifs TI</strong>, 
                  nous recommandons le niveau <strong className="text-purple-700">{levelNames[suggestedLevel]}</strong>:
                </p>
                
                <div className="space-y-2 text-xs text-gray-700">
                  {suggestedLevel === 'essentiel' && (
                    <>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Taille d'organisation parfaite pour ce niveau</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Rapport qualité-prix optimal pour PME</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Couvre l'essentiel pour démarrer</span>
                      </div>
                    </>
                  )}
                  {suggestedLevel === 'pro' && (
                    <>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Niveau idéal pour votre complexité TI</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Balance parfaite détail/prix</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Le choix de 70% de nos clients</span>
                      </div>
                    </>
                  )}
                  {suggestedLevel === 'expert' && (
                    <>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Infrastructure complexe = analyse poussée nécessaire</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Conformité réglementaire stricte</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>ROI maximisé sur investissement important</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Niveaux de qualité */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">3️⃣ Choisissez votre niveau de qualité</h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          {(['essentiel', 'pro', 'expert'] as QualityLevel[]).map((level) => {
            const pricing = documentType === 'audit' ? AUDIT_PRICING[level] : BILAN_PRICING[level];
            const price = calculatePrice(documentType === 'package' ? 'audit' : documentType, level);
            const isSelected = qualityLevel === level;
            
            return (
              <button
                key={level}
                onClick={() => setQualityLevel(level)}
                className={`p-6 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 shadow-xl scale-105'
                    : 'border-gray-200 hover:border-purple-300 hover:shadow-lg'
                } ${level === 'pro' ? 'relative' : ''}`}
              >
                {level === 'pro' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg">
                    ⭐ RECOMMANDÉ
                  </div>
                )}
                
                <div className="text-4xl mb-3">{pricing.icon}</div>
                <h4 className="text-xl font-bold mb-2">{pricing.name}</h4>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="w-4 h-4" />
                    {pricing.pages}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Award className="w-4 h-4" />
                    {pricing.duration}
                  </div>
                  <div className="flex items-center gap-2 text-sm font-bold text-purple-600">
                    <DollarSign className="w-4 h-4" />
                    {price.toLocaleString('fr-CA')} $
                  </div>
                </div>
                
                <div className="space-y-1">
                  {pricing.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {pricing.notIncluded && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 font-medium mb-1">Non inclus:</p>
                    {pricing.notIncluded.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs text-gray-400">
                        <X className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Comparaison détaillée */}
        {documentType !== 'package' && (
          <div className="mt-6">
            <button className="text-blue-600 hover:text-blue-800 font-medium text-sm underline">
              📊 Voir le tableau comparatif détaillé
            </button>
          </div>
        )}
      </div>

      {/* 🔥 POUR QUI? Profils clients par niveau */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-blue-600" />
          👥 Pour Qui? (Profils Clients Typiques)
        </h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          {/* Essentiel */}
          <div className="bg-white rounded-lg p-5 border-2 border-gray-200">
            <h4 className="font-bold text-md text-green-700 mb-3">📦 Essentiel</h4>
            <div className="space-y-2 text-xs text-gray-700">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>PME 5-50 employés</strong></span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Infrastructure TI simple/standardisée</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Pas de département TI interne</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Premier audit/bilan (découverte)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Budget limité (démarrage)</span>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="font-medium text-xs text-gray-500">Exemples:</p>
                <p className="text-xs text-gray-600 mt-1">Cabinet comptable, clinique dentaire, commerce détail</p>
              </div>
            </div>
          </div>

          {/* Pro */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-5 border-2 border-blue-400 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-md text-blue-700">🏆 Professionnel</h4>
              <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full font-bold">⭐ 70%</span>
            </div>
            <div className="space-y-2 text-xs text-gray-700">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span><strong>PME/ETI 50-200 employés</strong></span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Infrastructure hybride (cloud + on-premise)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>TI géré en externe ou petit département interne</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Conformité réglementaire (ISO, SOC2, PIPEDA)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Croissance rapide, besoin stratégie TI</span>
              </div>
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="font-medium text-xs text-gray-500">Exemples:</p>
                <p className="text-xs text-gray-600 mt-1">Cabinet avocats 75 employés, manufacturier, firme conseil</p>
              </div>
            </div>
          </div>

          {/* Expert */}
          <div className="bg-white rounded-lg p-5 border-2 border-purple-400">
            <h4 className="font-bold text-md text-purple-700 mb-3">💎 Expert</h4>
            <div className="space-y-2 text-xs text-gray-700">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span><strong>Grande organisation 200+ employés</strong></span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Infrastructure complexe multi-sites</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Département TI structuré (DSI, équipe)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Conformité stricte (finance, santé, gouvernement)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Enjeux critiques (cyber, business continuity)</span>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="font-medium text-xs text-gray-500">Exemples:</p>
                <p className="text-xs text-gray-600 mt-1">Institution financière, hôpital, grande municipalité</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🕐 Timeline & Livrables par niveau */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-amber-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-6 h-6 text-amber-600" />
          ⏱️ Timeline & Livrables Détaillés
        </h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          {/* Essentiel Timeline */}
          <div className="bg-white rounded-lg p-5 border-2 border-gray-200">
            <h4 className="font-bold text-md text-green-700 mb-3">📦 Essentiel</h4>
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-xs font-bold text-green-700">S1</div>
                  <span className="text-xs font-bold">Collecte & Analyse Préliminaire</span>
                </div>
                <p className="text-xs text-gray-600 ml-10">Questionnaire détaillé + collecte documentaire + première analyse</p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-xs font-bold text-green-700">S2</div>
                  <span className="text-xs font-bold">Analyse Approfondie</span>
                </div>
                <p className="text-xs text-gray-600 ml-10">Traitement IA + validation expert + scoring COBIT/NIST</p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-xs font-bold text-green-700">S3</div>
                  <span className="text-xs font-bold">Rédaction & Livraison</span>
                </div>
                <p className="text-xs text-gray-600 ml-10">Document 15-20 pages PDF + présentation exécutive 45 min</p>
              </div>
              
              <div className="mt-4 pt-3 border-t border-gray-200">
                <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-2">
                  ⏱️ Délai: <span className="text-green-600">2-3 semaines</span>
                </p>
                <p className="text-xs font-bold text-gray-700 mb-2 mt-3">📄 Contenu (15-20 pages):</p>
                <ul className="text-xs text-gray-600 space-y-1 ml-3">
                  <li>• Sommaire exécutif (2 pages)</li>
                  <li>• Évaluation par domaine (8-10 pages)</li>
                  <li>• Top 10 recommandations prioritaires</li>
                  <li>• Roadmap 12 mois (vue globale)</li>
                  <li>• Annexes essentielles</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Pro Timeline */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-5 border-2 border-blue-400 shadow-lg">
            <h4 className="font-bold text-md text-blue-700 mb-3">🏆 Professionnel</h4>
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-700">S1-2</div>
                  <span className="text-xs font-bold">Collecte Approfondie</span>
                </div>
                <p className="text-xs text-gray-600 ml-10">Questionnaire détaillé + entrevues parties prenantes (4-6h) + collecte documentaire</p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-700">S3-4</div>
                  <span className="text-xs font-bold">Analyse Multi-Niveaux</span>
                </div>
                <p className="text-xs text-gray-600 ml-10">Analyse IA avancée + validation consultant senior + scoring 39 domaines</p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-700">S5-6</div>
                  <span className="text-xs font-bold">Rédaction & Présentation</span>
                </div>
                <p className="text-xs text-gray-600 ml-10">Document 30-40 pages + roadmap 3 ans + présentation exécutive 2h</p>
              </div>
              
              <div className="mt-4 pt-3 border-t border-blue-200">
                <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-2">
                  ⏱️ Délai: <span className="text-blue-600">4-6 semaines</span>
                </p>
                <p className="text-xs font-bold text-gray-700 mb-2 mt-3">📄 Contenu (30-40 pages):</p>
                <ul className="text-xs text-gray-600 space-y-1 ml-3">
                  <li>• Sommaire exécutif détaillé (4 pages)</li>
                  <li>• Évaluation exhaustive 39 domaines</li>
                  <li>• 20-30 recommandations priorisées (coût/effort/ROI)</li>
                  <li>• Roadmap 3 ans avec jalons</li>
                  <li>• Matrices de risques + scénarios</li>
                  <li>• Annexes complètes + inventaires</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Expert Timeline */}
          <div className="bg-white rounded-lg p-5 border-2 border-purple-400">
            <h4 className="font-bold text-md text-purple-700 mb-3">💎 Expert</h4>
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">S1-3</div>
                  <span className="text-xs font-bold">Immersion Terrain Complète</span>
                </div>
                <p className="text-xs text-gray-600 ml-10">Questionnaire + entrevues multi-niveaux (10-15h) + visite sites + audits techniques sur place</p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">S4-8</div>
                  <span className="text-xs font-bold">Analyse Experte Multi-Niveaux</span>
                </div>
                <p className="text-xs text-gray-600 ml-10">Équipe experts dédiée (DSI, CyberSec, Compliance) + tests pénétration + revue pairs + validation</p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">S9-12</div>
                  <span className="text-xs font-bold">Rédaction & Livraison Premium</span>
                </div>
                <p className="text-xs text-gray-600 ml-10">Document 50-80 pages + roadmap 5 ans + présentation CA/comité 3h + session Q&A</p>
              </div>
              
              <div className="mt-4 pt-3 border-t border-gray-200">
                <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-2">
                  ⏱️ Délai: <span className="text-purple-600">8-12 semaines (2-3 mois)</span>
                </p>
                <p className="text-xs font-bold text-gray-700 mb-2 mt-3">📄 Contenu (50-80 pages):</p>
                <ul className="text-xs text-gray-600 space-y-1 ml-3">
                  <li>• Sommaire exécutif stratégique (6 pages)</li>
                  <li>• Évaluation complète 93 contrôles ISO 27001</li>
                  <li>• 40-60 recommandations détaillées (coût/ROI/timeline)</li>
                  <li>• Roadmap 5 ans stratégique</li>
                  <li>• Analyse réglementaire complète</li>
                  <li>• Scénarios d'attaque + tests pénétration</li>
                  <li>• Annexes exhaustives + livrables techniques</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 💰 Options de Paiement & Flexibilité */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-green-600" />
          💳 Options de Paiement & Garanties
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Options paiement */}
          <div className="bg-white rounded-lg p-5 border-2 border-gray-200">
            <h4 className="font-bold text-md text-gray-800 mb-3">Modalités de Paiement</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Paiement Comptant</p>
                  <p className="text-xs text-gray-600 mt-1">Économie de 5% sur le prix total</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Paiement 50/50</p>
                  <p className="text-xs text-gray-600 mt-1">50% à la signature, 50% à la livraison (option standard)</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Paiement 3 Versements</p>
                  <p className="text-xs text-gray-600 mt-1">Disponible pour niveau Expert uniquement (frais 3%)</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Crédit BZ (Clients Récurrents)</p>
                  <p className="text-xs text-gray-600 mt-1">Net 30 jours pour clients avec historique</p>
                </div>
              </div>
            </div>
          </div>

          {/* Garanties */}
          <div className="bg-white rounded-lg p-5 border-2 border-green-300">
            <h4 className="font-bold text-md text-green-700 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Nos Garanties Béton
            </h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 text-lg">
                  🎯
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Satisfaction 100%</p>
                  <p className="text-xs text-gray-600 mt-1">Si non satisfait, révision gratuite ou remboursement intégral</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 text-lg">
                  ⏰
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Délais Garantis</p>
                  <p className="text-xs text-gray-600 mt-1">Respect des délais ou 10% de rabais par jour de retard</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 text-lg">
                  🔒
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Confidentialité Absolue</p>
                  <p className="text-xs text-gray-600 mt-1">NDA signé, données chiffrées, destruction après projet</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 text-lg">
                  🤝
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Support Post-Livraison</p>
                  <p className="text-xs text-gray-600 mt-1">30 jours support gratuit pour questions/clarifications</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bonus section */}
        <div className="mt-6 bg-gradient-to-r from-yellow-100 to-amber-100 border-2 border-yellow-400 rounded-lg p-4">
          <h4 className="font-bold text-md text-amber-900 mb-2 flex items-center gap-2">
            <Gift className="w-5 h-5" />
            🎁 Bonus Inclus (Tous Niveaux)
          </h4>
          <div className="grid md:grid-cols-3 gap-3 text-xs text-gray-700">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span>Session présentation incluse</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span>Version PDF + PowerPoint</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span>Templates recommandations</span>
            </div>
          </div>
        </div>
      </div>

      {/* 💬 Témoignages Clients par Niveau */}
      <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-6 border-2 border-pink-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-pink-600" />
          💬 Ce Que Disent Nos Clients
        </h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          {/* Témoignage Essentiel */}
          <div className="bg-white rounded-lg p-5 border-2 border-gray-200 relative">
            <div className="absolute -top-3 -left-3 w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center text-2xl shadow-lg">
              💬
            </div>
            <div className="mt-4">
              <p className="text-xs italic text-gray-700 mb-3">
                "Pour notre clinique de 15 employés, le niveau Essentiel était parfait. Rapport clair, 
                recommandations concrètes, et surtout un prix abordable pour une PME. On a immédiatement 
                appliqué 8 des 10 recommandations!"
              </p>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-xs font-bold text-green-700">
                  MC
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800">Marie-Claude T.</p>
                  <p className="text-xs text-gray-500">Directrice, Clinique Dentaire St-Laurent</p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>
          </div>

          {/* Témoignage Pro */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-5 border-2 border-blue-400 shadow-lg relative">
            <div className="absolute -top-3 -left-3 w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center text-2xl shadow-lg">
              💬
            </div>
            <div className="mt-4">
              <p className="text-xs italic text-gray-700 mb-3">
                "Le niveau Pro nous a permis d'obtenir notre certification ISO 27001 en 18 mois au lieu de 3 ans. 
                L'analyse de 39 domaines était exactement ce dont on avait besoin. ROI de 320% la première année!"
              </p>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-blue-200">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-700">
                  JF
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800">Jean-François L.</p>
                  <p className="text-xs text-gray-500">VP TI, Cabinet Juridique Laflamme (85 emp.)</p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>
          </div>

          {/* Témoignage Expert */}
          <div className="bg-white rounded-lg p-5 border-2 border-purple-400 relative">
            <div className="absolute -top-3 -left-3 w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-2xl shadow-lg">
              💬
            </div>
            <div className="mt-4">
              <p className="text-xs italic text-gray-700 mb-3">
                "Niveau Expert = investissement majeur, mais ça nous a sauvé une cyber-attaque potentielle 
                (identifiée dans les scénarios). Les 93 contrôles ISO + tests pénétration nous ont fait réaliser 
                qu'on était vulnérables. Retour sur investissement : incalculable."
              </p>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">
                  SB
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800">Sophie B.</p>
                  <p className="text-xs text-gray-500">DSI, Coopérative Financière Desjardins (250 emp.)</p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Prix total */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl p-8 shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90 mb-1">Prix estimé</p>
            <p className="text-5xl font-bold">{getTotalPrice().toLocaleString('fr-CA')} $</p>
            <p className="text-sm opacity-90 mt-2">
              {documentType === 'package' && '✨ Économie de 15% déjà appliquée!'}
            </p>
          </div>
          <Award className="w-24 h-24 opacity-20" />
        </div>
        
        <button className="w-full mt-6 bg-white text-green-600 font-bold py-4 rounded-lg hover:bg-green-50 transition-all flex items-center justify-center gap-2">
          <Download className="w-5 h-5" />
          Télécharger l'offre de service PDF
        </button>
      </div>
    </motion.div>
  );
}

